import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { fetchKassaDay } from '../adminApi'
import { isIsoDate, today } from '../../core/format/dates'
import type { RequestOptions } from '../../core/types/backend'

/**
 * De kassa van één dag: dag in het adres (?dag=, leeg = vandaag), de bonnen en afspraken van de
 * server (die rekent; het scherm telt niets op). `?afspraak=` opent meteen een bon voor die afspraak
 * (vanuit de agenda).
 */
export function useKassa() {
  const [params, setParams] = useSearchParams()
  const now = today()
  const asked = params.get('dag')
  const day = asked && isIsoDate(asked) && asked <= now ? asked : now
  const appointmentId = params.get('afspraak')

  const load = useCallback((opts: RequestOptions) => fetchKassaDay(day, opts), [day])
  const { data, status, reload, refresh } = useLatestLoad(load)

  const setDay = useCallback(
    (next: string) =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (next >= now) p.delete('dag')
          else p.set('dag', next)
          p.delete('afspraak')
          return p
        },
        { replace: true },
      ),
    [setParams, now],
  )

  const clearAppointment = useCallback(
    () =>
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          p.delete('afspraak')
          return p
        },
        { replace: true },
      ),
    [setParams],
  )

  return { day, isToday: day === now, appointmentId, data, status, reload, refresh, setDay, clearAppointment }
}
